import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Maximize, Minimize } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Link } from 'react-router-dom';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionImg = motion.img;
const MotionH1 = motion.h1;
const MotionSpan = motion.span;
const MotionP = motion.p;
const MotionButton = motion.button;

interface CarouselSlide {
  id: number;
  image: string;
  title: string;
  subtitle: string;
  description?: string;
}

interface HeroCarouselProps {
  slides: CarouselSlide[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  className?: string;
}

export function HeroCarousel({ 
  slides, 
  autoPlay = true, 
  autoPlayInterval = 3000, // 3-second transitions for perfect rhythm
  className 
}: HeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true); // Always start playing
  const [isPaused, setIsPaused] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fullscreen functionality
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Preload images
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = slides.map((slide) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = slide.image;
        });
      });

      try {
        await Promise.all(imagePromises);
        setIsLoaded(true);
      } catch (error) {
        console.warn('Some images failed to preload:', error);
        setIsLoaded(true); // Continue anyway
      }
    };

    if (slides.length > 0) {
      preloadImages();
    }
  }, [slides]);

  // Touch gesture handling
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Auto-play functionality with pause on hover - ensure it starts immediately
  useEffect(() => {
    if (!slides?.length) return;

    const interval = setInterval(() => {
      if (!isPaused) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlayInterval, slides?.length, isPaused]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(!isPlaying);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, nextSlide, prevSlide]);

  // Return early if no slides
  if (!slides || slides.length === 0) {
    return (
      <div className={cn("relative w-full h-screen bg-gradient-to-br from-navy via-primary to-ocean flex items-center justify-center", className)}>
        <div className="text-center">
          <h2 className="text-white text-2xl mb-4">No carousel slides available</h2>
          <p className="text-white/70">Please add slides to display the carousel</p>
        </div>
      </div>
    );
  }

  const slideVariants = {
    enter: {
      opacity: 0,
      scale: 1.05,
    },
    center: {
      zIndex: 1,
      opacity: 1,
      scale: 1,
    },
    exit: {
      zIndex: 0,
      opacity: 0,
      scale: 1.05,
    },
  };

  return (
    <div 
      className={cn("relative w-full h-screen overflow-hidden bg-black", className)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Loading state */}
      {!isLoaded && (
        <div className="absolute inset-0 z-50 bg-black flex items-center justify-center">
          <div className="text-center">
            <MotionDiv
              className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <p className="text-white text-lg font-medium">Loading Experience...</p>
          </div>
        </div>
      )}

      {/* Main carousel container with seamless transitions */}
      <div className="relative w-full h-full">
        <AnimatePresence mode="sync">
          <MotionDiv
            key={currentSlide}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              opacity: { duration: 0.8, ease: "easeInOut" },
              scale: { duration: 0.8, ease: "easeInOut" },
            }}
            className="absolute inset-0"
          >
            {/* Background Image with Enhanced Seamless Transition */}
            <MotionDiv
              className="absolute inset-0 z-0"
              initial={{ scale: 1.05, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                scale: { duration: autoPlayInterval / 1000, ease: "easeOut" },
                opacity: { duration: 0.8, ease: "easeInOut" }
              }}
            >
              {/* Subtle gradient overlay for dahnay.com style */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/50 z-10" />
              <MotionImg
                src={slides[currentSlide].image}
                alt={slides[currentSlide].title}
                className="w-full h-full object-cover"
                initial={{ filter: "blur(0px) brightness(0.8)" }}
                animate={{ filter: "blur(0px) brightness(0.8)" }}
                transition={{ duration: 0 }}
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.background = 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2a2a2a 100%)';
                  }
                }}
              />
              {/* Minimal additional overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20 z-20" />
            </MotionDiv>

            {/* Enhanced Content Overlay with Left-Aligned Layout like dahnay.com */}
            <div className="relative z-30 h-full flex items-center">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl">
                  <MotionDiv
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="space-y-6"
                  >
                    {/* Tagline/Subtitle First - Thin and Modern */}
                    <MotionP
                      className="text-base md:text-lg text-white/90 font-light tracking-wider uppercase"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      style={{
                        fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                        fontWeight: '300',
                        letterSpacing: '0.1em',
                        textShadow: "1px 1px 4px rgba(0, 0, 0, 0.6)",
                      }}
                    >
                      {slides[currentSlide].subtitle}
                    </MotionP>

                    {/* Main Heading - Thin and Modern */}
                    <MotionH1
                      className="text-3xl md:text-5xl lg:text-6xl font-light leading-tight text-white tracking-wide"
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -30, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      style={{
                        fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                        fontWeight: '200',
                        letterSpacing: '0.02em',
                        textShadow: "2px 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <MotionSpan
                        animate={{ 
                          opacity: [0.9, 1, 0.9]
                        }}
                        transition={{ 
                          duration: 4, 
                          repeat: Infinity, 
                          ease: "easeInOut" 
                        }}
                      >
                        {slides[currentSlide].title}
                      </MotionSpan>
                    </MotionH1>

                    {/* Description Text - Clean and Readable */}
                    {slides[currentSlide].description && (
                      <MotionP
                        className="text-base md:text-lg text-white/85 max-w-2xl leading-relaxed font-light"
                        initial={{ y: 15, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -15, opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        style={{
                          fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
                          fontWeight: '300',
                          lineHeight: '1.6',
                          textShadow: "1px 1px 4px rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {slides[currentSlide].description}
                      </MotionP>
                    )}

                    {/* CTA Button Section - Left-aligned like dahnay.com */}
                    <MotionDiv
                      className="pt-8"
                      initial={{ y: 15, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -15, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                      <Link
                        to="/services"
                        className="relative bg-gradient-to-r from-ocean to-accent hover:from-ocean-dark hover:to-accent-dark text-white font-semibold py-4 px-8 rounded-full text-base shadow-xl transition-all duration-300 overflow-hidden group inline-flex items-center"
                        style={{ display: 'inline-block' }}
                      >
                        {/* Subtle shimmer effect */}
                        <MotionDiv
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                          initial={{ x: "-100%" }}
                          animate={{ x: "100%" }}
                          transition={{ 
                            duration: 3, 
                            repeat: Infinity, 
                            repeatDelay: 2,
                            ease: "linear" 
                          }}
                        />
                        <span className="relative z-10 flex items-center space-x-2">
                          <span>Explore Our Services</span>
                          <MotionSpan
                            animate={{ x: [0, 3, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            →
                          </MotionSpan>
                        </span>
                      </Link>
                    </MotionDiv>
                  </MotionDiv>
                </div>
              </div>
            </div>
          </MotionDiv>
        </AnimatePresence>
      </div>

      {/* Modern Minimal Navigation Arrows */}
      <MotionButton
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-40 bg-black/20 backdrop-blur-sm hover:bg-black/30 text-white p-2 sm:p-3 rounded-full border border-white/10 transition-all duration-200 group opacity-70 hover:opacity-100"
        onClick={prevSlide}
        whileHover={{ scale: 1.05, x: -2 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0.7, x: 0 }}
        animate={{ opacity: 0.7, x: 0 }}
        transition={{ delay: 0 }}
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
      </MotionButton>

      <MotionButton
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-40 bg-black/20 backdrop-blur-sm hover:bg-black/30 text-white p-2 sm:p-3 rounded-full border border-white/10 transition-all duration-200 group opacity-70 hover:opacity-100"
        onClick={nextSlide}
        whileHover={{ scale: 1.05, x: 2 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0.7, x: 0 }}
        animate={{ opacity: 0.7, x: 0 }}
        transition={{ delay: 0 }}
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
      </MotionButton>

      {/* Enhanced Control Panel */}
      <MotionDiv
        className="absolute top-6 right-6 z-40 flex space-x-2"
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0 }}
      >
        {/* Play/Pause Button */}
        <button
          className="p-1.5 bg-transparent text-white opacity-60 hover:opacity-90 transition-opacity duration-150 rounded focus:outline-none"
          onClick={() => setIsPlaying(!isPlaying)}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>

        {/* Fullscreen Button */}
        <button
          className="p-1.5 bg-transparent text-white opacity-60 hover:opacity-90 transition-opacity duration-150 rounded focus:outline-none"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? (
            <Minimize className="w-5 h-5" />
          ) : (
            <Maximize className="w-5 h-5" />
          )}
        </button>
      </MotionDiv>

      {/* Enhanced Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-40 h-2 bg-white/10">
        <MotionDiv
          className="h-full bg-gradient-to-r from-ocean via-accent to-ocean-light shadow-lg"
          initial={{ width: "0%" }}
          animate={{ width: isPlaying && !isPaused ? "100%" : "0%" }}
          transition={{
            duration: autoPlayInterval / 1000,
            ease: "linear",
            repeat: isPlaying && !isPaused ? Infinity : 0,
          }}
          key={`${currentSlide}-${isPlaying}-${isPaused}`}
        />
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-40 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-white shadow-lg scale-125' 
                : 'bg-white/40 hover:bg-white/60'
            }`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Keyboard shortcuts hint */}
      <MotionDiv
        className="absolute bottom-6 right-6 z-40 bg-white/10 backdrop-blur-lg text-white px-3 py-2 rounded-lg border border-white/20 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
      >
        <div className="flex items-center space-x-2 text-xs">
          <span>←→</span>
          <span className="opacity-70">Navigate</span>
          <span className="mx-1 opacity-50">|</span>
          <span>Space</span>
          <span className="opacity-70">Pause</span>
        </div>
      </MotionDiv>

      {/* Minimal Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
        {[...Array(8)].map((_, i) => (
          <MotionDiv
            key={`${currentSlide}-${i}`}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              x: [0, 100, 200, 100, 0],
              y: [0, -100, -200, -100, 0],
              opacity: [0, 0.4, 0.6, 0.4, 0],
              scale: [0, 1, 1, 1, 0],
            }}
            transition={{
              duration: 15 + i * 0.5,
              repeat: Infinity,
              delay: i * 1,
              ease: "easeInOut",
            }}
            style={{
              left: `${10 + i * 10}%`,
              top: "80%",
              filter: "blur(0.5px)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default HeroCarousel;


